import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import { Stack } from "components/icons/generated";
import MetaInfoList from "components/MetaInfoList";
import MetaInfoListItem from "components/MetaInfoList/Item";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import ViewHeader from "components/ViewHeader";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { canDeleteDeployment } from "shared/Templates/access/canDeleteDeployment";
import { Template, TemplateDeployment } from "types/generated";
import { showDeleteConfirmation } from "views/Account/Template/Deployments/DeleteConfirmation";
import { AccountContext } from "views/AccountWrapper";
import DeploymentStateCallout from "components/DeploymentStateCallout";

import DeploymentStateBadge from "../../Template/Deployments/StateBadge";
import TemplateDeploymentOutputsEmpty from "../../TemplateDeployment/Outputs/Empty";
import { getDeploymentOutputs } from "../../TemplateDeployment/Outputs/helpers";
import TemplateDeploymentOutputsTable from "../../TemplateDeployment/Outputs/Table";

type PageLayoutProps = {
  deployment: TemplateDeployment;
  template: Template;
};

const PageLayout = ({ deployment, template }: PageLayoutProps) => {
  const { accountName } = useTypedContext(AccountContext);
  const navigate = useNavigate();

  const deleteAccess = canDeleteDeployment(deployment);

  useTitle(`Templates · ${deployment.name} · ${accountName}`);

  const outputs = useMemo(() => getDeploymentOutputs(deployment), [deployment]);

  const handleDelete = useCallback(() => {
    showDeleteConfirmation({
      name: deployment.name,
      id: deployment.id,
      templateID: template.id,
      onDelete: () => {
        navigate(`/templates/template/${template.id}`);
      },
    });
  }, [deployment, navigate, template]);

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>{deployment.name}</ViewHeaderTitle>
            <DeploymentStateBadge state={deployment.state} />
          </ViewHeaderWrapper>

          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <DropdownMenuEllipsis tooltip="Deployment actions" dotsSize="medium">
              <CopyFieldDropdownMenuItem title="Copy ID" value={deployment.id} />
              <DropdownMenuItem
                onAction={handleDelete}
                danger
                isDisabled={!deleteAccess.result}
                tooltip={deleteAccess.message}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuEllipsis>
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>

        <ViewHeaderScrollCollapse>
          <MetaInfoList>
            <MetaInfoListItem icon={Stack}>{deployment.templateVersion.version}</MetaInfoListItem>

            <TimestampMetaInfo timestamp={deployment.createdAt} />
          </MetaInfoList>
        </ViewHeaderScrollCollapse>
      </ViewHeader>
      <DeploymentStateCallout state={deployment.state} />

      {outputs.length === 0 ? (
        <TemplateDeploymentOutputsEmpty />
      ) : (
        <TemplateDeploymentOutputsTable outputs={outputs} />
      )}
    </>
  );
};

export default PageLayout;
